import Button from "@/components/bricks/button/Button";
import { Link } from "react-router-dom";
import EditColumnsIcon from "../components/icons/EditColumnsIcon";
import SortDefaultIcon from "../components/icons/SortDefaultIcon";
import SortDownIcon from "../components/icons/SortDownIcon";
import SortUpIcon from "../components/icons/SortUpIcon";
import { ModalTrigger } from "../components/Modal/Modal";
import {
  durationStringToTimeString,
  stringDateToHalvAar,
  stringDateToKvartal,
  stringDateToPP,
} from "./date";
import { getFieldValue } from "./form";
import { IColumn, SortBy, ITextFieldFormat } from "../interfaces/table";
import { StyleHTMLAttributes } from "react";

export function getActiveColumnFields<T>(columns: IColumn<T>[]) {
  return columns.filter((p) => p.active).map((p) => p.field);
}

export function createColumnEditorTrigger(props?: { onClick?: Function }) {
  return (
    <ModalTrigger asChild>
      <Button
        icon
        transparent
        aria-label="Redigér kolonner"
        className="tooltip-center"
        color="primary"
        onClick={props?.onClick ? props.onClick : () => {}}
      >
        <EditColumnsIcon width={17} />
      </Button>
    </ModalTrigger>
  );
}

function getActiveColumns<T>(columns: IColumn<T>[]) {
  return columns.filter((c) => c.active).map((c) => c.field);
}

export type Sortings = {
  sortBy: SortBy[];
  setSortBy: React.Dispatch<React.SetStateAction<SortBy[]>>;
  defaultSortBy: SortBy[];
};

export function getHeaderActiveColumns<T>(
  columns: IColumn<T>[],
  sortings?: Sortings,
  style?: StyleHTMLAttributes<any>,
) {
  return columns
    .filter((c) => getActiveColumns(columns).indexOf(c.field) > -1)
    .map((col) => {
      if (!sortings) {
        return (
          <th
            className={`${col.todo ? "todo" : ""}`}
            style={style}
            key={col.field as any}
            scope="col"
          >
            {col.title}
          </th>
        );
      }
      return (
        <SortableTH
          key={col.field as any}
          field={col.field as unknown as string}
          sortings={sortings}
          title={col.title}
          todo={col.todo}
        />
      );
    });
}

export function SortableTH({
  field,
  sortings,
  title,
  todo,
}: {
  title: string;
  field: string;
  sortings: Sortings;
  todo?: boolean;
}) {
  return (
    <th className={`${todo ? "todo" : ""}`} scope="col">
      <button
        className="btn-sort"
        onClick={() => sortTableByHeaderField(field, sortings)}
      >
        <SortArrow sortBy={sortings.sortBy[0]} field={field} />
        <span className="btn-sort__title">{title}</span>
      </button>
    </th>
  );
}

function SortArrow({ field, sortBy }: { sortBy: SortBy; field: string }) {
  if (field !== sortBy.field) {
    return (
      <span className="btn-sort__arrow unsorted" aria-hidden="true">
        <SortDefaultIcon />
      </span>
    );
  }
  if (sortBy.direction === "asc") {
    return (
      <span className="btn-sort__arrow sorted" aria-hidden="true">
        <SortDownIcon />
      </span>
    );
  }
  return (
    <span className="btn-sort__arrow sorted" aria-hidden="true">
      <SortUpIcon />
    </span>
  );
}

function TDLink(props: { textField: string; todo?: boolean; link: string }) {
  const { todo, textField, link } = props;
  return (
    <td className={`${todo ? "todo" : ""}`}>
      <Link className="link-primary" to={link}>
        {textField}
      </Link>
    </td>
  );
}
function TDExternalLink(props: {
  textField: string;
  todo?: boolean;
  link: string;
}) {
  const { todo, textField, link } = props;
  return (
    <td className={`${todo ? "todo" : ""}`}>
      <a className="link-primary" href={link}>
        {textField}
      </a>
    </td>
  );
}

export function TD<T>({ col, rowData }: { rowData: any; col: IColumn<T> }) {
  let data = getFieldValue(col.field, rowData);

  if (col.format === "custom" && col.customValue) {
    data = col.customValue(data);
  }

  const textField = formatTextField(data, col.format);
  if (col.link) {
    return <TDLink link={col.link(rowData)} textField={textField as string} />;
  }
  if (col.externalLink) {
    return (
      <TDExternalLink
        link={col.externalLink(rowData)}
        textField={textField as string}
      />
    );
  }

  return <td className={`${col.todo ? "todo" : ""}`}>{textField}</td>;
}

export function formatTextField(
  inputText: string | number | boolean | null,
  format: ITextFieldFormat,
) {
  switch (format) {
    case "date":
      return inputText ? stringDateToPP(inputText.toString()) : "";
    case "date_halfyear":
      return inputText ? stringDateToHalvAar(inputText.toString()) : "";
    case "date_quarter":
      return inputText ? stringDateToKvartal(inputText.toString()) : "";
    case "boolean":
      if (inputText) return <span>Ja</span>;
      else return <span>Nej</span>;
    case "duration":
      return inputText ? durationStringToTimeString(inputText.toString()) : "-";
    default:
      return inputText;
  }
}

const sortTableByHeaderField = (field: string, sortings: Sortings) => {
  const { defaultSortBy, setSortBy, sortBy } = sortings;
  if (
    field === defaultSortBy[0].field &&
    sortBy[0].direction !== defaultSortBy[0].direction
  ) {
    setSortBy(defaultSortBy);
  } else if (field !== sortBy[0].field) {
    setSortBy([{ direction: "asc", field: field as unknown as string }]);
  } else {
    setSortBy([
      {
        direction: sortBy[0].direction === "asc" ? "desc" : "asc",
        field: field as unknown as string,
      },
    ]);
  }
};
